import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog, faArrowRightFromBracket, faBook, faIdCard, faShieldAlt, faExclamationTriangle, faFileContract, faCookieBite } from '@/fontawesome';
import { useUser } from '@/contexts/UserContext';
import * as CookieConsent from 'vanilla-cookieconsent';

interface UserAvatarProps {
  onLogout: () => void;
  collapsed?: boolean;
}

export default function UserAvatar({ onLogout, collapsed = false }: UserAvatarProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user, isAdmin } = useUser();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user initials
  const getInitials = () => {
    if (!user) return 'U';
    const firstInitial = user.firstName?.charAt(0) || '';
    const lastInitial = user.lastName?.charAt(0) || '';
    return (firstInitial + lastInitial).toUpperCase();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    onLogout();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={toggleDropdown}
        className={`flex items-center ${collapsed ? 'justify-center' : ''} p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer w-full`}
        title={collapsed ? (user ? `${user.firstName} ${user.lastName}` : 'User') : undefined}
      >
        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-extrabold text-white">
            {getInitials()}
          </span>
        </div>
        <span className={`ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 truncate whitespace-nowrap transition-all duration-300 ${collapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`}>
          {user ? `${user.firstName} ${user.lastName}` : 'User'}
        </span>
      </button>

      {/* Dropdown Menu - opens upward */}
      {isDropdownOpen && (
        <div className={`absolute ${collapsed ? 'left-full ml-2 bottom-0' : 'right-0 bottom-full mb-2'} w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50`}>
          <div className="py-2">
            {/* User Info */}
            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-sm font-extrabold text-white">
                    {getInitials()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user ? `${user.firstName} ${user.lastName}` : 'User'}
                  </p>
                  {isAdmin && (
                    <p className="text-xs text-blue-600 dark:text-blue-400">
                      Administrator
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-1">
              <Link
                to="/user-settings"
                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setIsDropdownOpen(false)}
              >
                <FontAwesomeIcon icon={faCog} className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="truncate">Benutzereinstellungen</span>
              </Link>

              {/* Swagger Documentation - only for admins */}
              {isAdmin && (
                <a
                  href={`${import.meta.env.VITE_SWAGGER_URL}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => setIsDropdownOpen(false)}
                >
                  <FontAwesomeIcon icon={faBook} className="w-4 h-4 mr-3 flex-shrink-0" />
                  <span className="truncate">API Dokumentation</span>
                </a>
              )}

              {/* Legal Links */}
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

              <a
                href="/impressum"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setIsDropdownOpen(false)}
              >
                <FontAwesomeIcon icon={faIdCard} className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="truncate">Impressum</span>
              </a>

              <a
                href="/agb"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setIsDropdownOpen(false)}
              >
                <FontAwesomeIcon icon={faFileContract} className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="truncate">AGB</span>
              </a>

              <a
                href="/datenschutz"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setIsDropdownOpen(false)}
              >
                <FontAwesomeIcon icon={faShieldAlt} className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="truncate">Datenschutz</span>
              </a>

              <a
                href="/risikodisclaimer"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => setIsDropdownOpen(false)}
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="truncate">Risikohinweis</span>
              </a>

              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  CookieConsent.showPreferences();
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <FontAwesomeIcon icon={faCookieBite} className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="truncate">Cookie-Einstellungen</span>
              </button>

              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <FontAwesomeIcon icon={faArrowRightFromBracket} className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="truncate">Abmelden</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
